import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Mail, RefreshCw, Pencil, CheckCircle2, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  resendVerificationEmail,
  changeAccountEmail,
} from "@/lib/onboarding.functions";
import { friendlyAuthError } from "@/lib/friendly-errors";
import {
  PENDING_VERIFY_EMAIL_KEY,
  clearPendingVerifyEmail,
  readVerifiedStatus,
} from "@/lib/verify-status";

export const Route = createFileRoute("/verify-email")({
  head: () => ({
    meta: [
      { title: "Confirm your email address — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Confirm your email address to unlock your Recovery Trauma™ account. We send a confirmation link — not a code.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const navigate = useNavigate();
  const resend = useServerFn(resendVerificationEmail);
  const changeEmail = useServerFn(changeAccountEmail);

  const [email, setEmail] = useState<string | null>(null);
  const [hasSession, setHasSession] = useState(false);
  const [checking, setChecking] = useState(false);
  const [verified, setVerified] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const pollRef = useRef<number | null>(null);

  // Advance an already-verified viewer to the next step in their journey
  // and wipe the stale pending marker so returning users can never be
  // trapped on this screen by leftover sessionStorage.
  function advanceVerified() {
    clearPendingVerifyEmail();
    setVerified(true);
    setTimeout(
      () => navigate({ to: "/membership", replace: true }),
      600,
    );
  }

  // Initial load: prefer the locally-stored Supabase session (synchronous,
  // and carries email_confirmed_at) so a verified viewer is never mis-classified
  // as "still awaiting confirmation" during a slow getUser() call.
  useEffect(() => {
    const stored = readVerifiedStatus();
    if (stored.verified) {
      setEmail(stored.email);
      setHasSession(true);
      advanceVerified();
      return;
    }

    let pending: string | null = null;
    try {
      pending = sessionStorage.getItem(PENDING_VERIFY_EMAIL_KEY);
    } catch {
      /* noop */
    }

    supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        setHasSession(true);
        setEmail(data.user.email ?? stored.email ?? pending ?? null);
        if (data.user.email_confirmed_at) {
          advanceVerified();
        }
        return;
      }
      // No live user AND no stored session AND no pending marker →
      // there is nothing to verify. Send them to login.
      if (!stored.hasStoredUser && !pending) {
        navigate({ to: "/login", replace: true });
        return;
      }
      // Otherwise render the "check your email" fallback for the address
      // we know about, but keep re-checking (see the recovery poller below).
      setEmail(stored.email ?? pending ?? null);
      if (stored.hasStoredUser) setHasSession(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Recovery poller. Runs regardless of whether the initial getUser()
  // resolved to a session — a transient auth-client failure must not
  // permanently strand a verified viewer here. Every 4s we re-check
  // both the locally stored session and (if available) getUser().
  useEffect(() => {
    if (verified) return;
    pollRef.current = window.setInterval(async () => {
      // Cheap synchronous re-read first.
      const stored = readVerifiedStatus();
      if (stored.verified) {
        window.clearInterval(pollRef.current!);
        setHasSession(true);
        setEmail((e) => e ?? stored.email);
        advanceVerified();
        return;
      }
      setChecking(true);
      const { data } = await supabase.auth.getUser().catch(() => ({ data: { user: null } }));
      setChecking(false);
      if (data.user) {
        setHasSession(true);
        setEmail((e) => e ?? data.user!.email ?? null);
        if (data.user.email_confirmed_at) {
          window.clearInterval(pollRef.current!);
          advanceVerified();
        }
      }
    }, 4000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verified]);

  // React to tab-focus / auth-state changes so we recover the moment the
  // user returns from clicking the link (or signs in in another tab).
  useEffect(() => {
    function recheck() {
      const stored = readVerifiedStatus();
      if (stored.verified) advanceVerified();
    }
    document.addEventListener("visibilitychange", recheck);
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) setHasSession(true);
      if (
        (event === "SIGNED_IN" || event === "USER_UPDATED" || event === "TOKEN_REFRESHED") &&
        session?.user?.email_confirmed_at
      ) {
        advanceVerified();
      }
    });
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cooldown timer for resend.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  async function handleResend() {
    setErr(null);
    setMsg(null);
    // Guard: never call the Supabase resend endpoint for an already-verified
    // viewer. Supabase rejects the call, we'd previously surface a confusing
    // error, and no email is ever sent.
    const stored = readVerifiedStatus();
    if (stored.verified) {
      advanceVerified();
      return;
    }
    try {
      if (hasSession) {
        await resend({ data: undefined as never });
      } else if (email) {
        // No session yet — use the public resend endpoint keyed by email.
        const { error } = await supabase.auth.resend({ type: "signup", email });
        if (error) throw error;
      } else {
        throw new Error("We couldn't find your email address.");
      }
      setMsg("If this address still needs confirmation, a new email is on its way. If you've already clicked a confirmation link, sign in instead.");
      setCooldown(60);
    } catch (e) {
      setErr(friendlyAuthError(e));
    }
  }

  async function handleChangeEmail(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setMsg(null);
    setSavingEmail(true);
    try {
      const res = await changeEmail({ data: { email: newEmail.trim() } });
      setEmail(res.email);
      try {
        sessionStorage.setItem(PENDING_VERIFY_EMAIL_KEY, res.email);
      } catch {
        /* noop */
      }
      setEditing(false);
      setNewEmail("");
      setMsg("We've sent a confirmation link to your new address.");
      setCooldown(60);
    } catch (e2) {
      setErr(friendlyAuthError(e2));
    } finally {
      setSavingEmail(false);
    }
  }

  if (verified) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-lg items-center justify-center px-4">
        <div className="w-full rounded-3xl border border-gold/40 bg-card p-8 text-center shadow-[var(--shadow-gold)]">
          <CheckCircle2 className="mx-auto h-12 w-12 text-[var(--gold-deep)]" aria-hidden="true" />
          <h1 className="mt-4 font-display text-2xl text-foreground">Email confirmed 💛</h1>
          <p className="mt-2 text-sm text-foreground/70">Taking you to your account…</p>
          <Loader2 className="mx-auto mt-4 h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[75vh] max-w-lg items-center justify-center px-4 py-10">
      <div className="w-full rounded-3xl border border-gold/30 bg-card p-8 text-center shadow-[var(--shadow-gold)] sm:p-10">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
          <Mail className="h-7 w-7 text-foreground" strokeWidth={2} aria-hidden="true" />
        </div>
        <p className="mx-auto mt-2 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--gold-deep)]">
          Step 2 of 4
        </p>
        <h1 className="mt-4 font-display text-2xl text-foreground sm:text-3xl">
          Confirm your email address
        </h1>
        <div className="mx-auto mt-4 max-w-sm rounded-2xl border border-gold/50 bg-[oklch(0.97_0.02_85)] px-4 py-3 text-start text-[13px] leading-relaxed text-foreground">
          <p className="font-semibold text-foreground">
            Your account has been created — but access isn't unlocked yet.
          </p>
          <p className="mt-1 text-foreground/80">
            Confirming your email is just the next step. To unlock the
            Recovery Trauma™ platform, you'll then need to complete your
            Membership.
          </p>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-foreground/75 sm:text-base">
          We've sent a confirmation link to
        </p>
        <p className="mt-1 break-words text-sm font-medium text-foreground">
          {email ?? "your address"}
        </p>
        <p className="mx-auto mt-3 max-w-sm text-xs leading-relaxed text-muted-foreground">
          Recovery Trauma™ uses a confirmation <strong>link</strong>, not a code.
          Open the email and tap the link — this page will continue on its own
          the moment we detect your address is confirmed.
        </p>
        <div className="mx-auto mt-5 max-w-sm rounded-2xl border border-gold/30 bg-cream/50 px-4 py-3 text-start text-[13px] leading-relaxed text-foreground/80">
          <p className="font-semibold text-foreground">Your journey</p>
          <ul className="mt-1.5 space-y-1">
            <li>✅ Step 1 — Account created</li>
            <li>⏳ Step 2 — Confirm your email address</li>
            <li className="text-foreground/60">💳 Step 3 — Become a Member</li>
            <li className="text-foreground/60">🔓 Step 4 — Access the Recovery Trauma™ platform</li>
          </ul>
          <p className="mt-2 text-[12px] text-foreground/70">
            Confirming your email does <strong>not</strong> activate your
            membership. After you confirm, you'll be invited to complete your
            Membership to unlock the platform.
          </p>
        </div>

        {msg && (
          <p className="mt-4 rounded-lg bg-[oklch(0.97_0.02_85)] px-3 py-2 text-xs text-[var(--gold-deep)]">
            {msg}
          </p>
        )}
        {err && (
          <p className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {err}
          </p>
        )}

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <a
            href="mailto:"
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-5 py-3 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)]"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            Open Email App
          </a>
          <button
            type="button"
            onClick={handleResend}
            disabled={false}
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-gold/40 bg-card px-5 py-3 text-sm font-medium text-foreground/85 transition hover:border-gold disabled:opacity-60"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Resend Email

          </button>
          {hasSession && !editing && (
            <button
              type="button"
              onClick={() => {
                setEditing(true);
                setNewEmail(email ?? "");
              }}
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-border bg-background px-5 py-3 text-sm font-medium text-foreground/75 transition hover:border-gold/40"
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Change Email
            </button>
          )}
        </div>

        {editing && (
          <form onSubmit={handleChangeEmail} className="mt-6 space-y-3 text-start">
            <label className="block text-xs font-medium text-foreground/80">
              New email address
              <input
                type="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                autoComplete="email"
                className="mt-1 w-full min-h-[44px] rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-gold"
              />
            </label>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={savingEmail || !newEmail}
                className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-full bg-[var(--gradient-gold)] px-4 text-sm font-semibold text-foreground disabled:opacity-60"
              >
                {savingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & resend"}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-border bg-background px-4 text-sm font-medium text-foreground/70"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          {checking && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
          <span>Waiting for confirmation…</span>
        </div>

        {/* Always-visible escape hatch: if the viewer knows they are already
            confirmed, let them continue without arguing with them. The
            /membership guard performs its own verified check and will
            forward them onwards. */}
        <div className="mt-6">
          <button
            type="button"
            onClick={() => {
              clearPendingVerifyEmail();
              navigate({
                to: "/membership",
                replace: true,
              });
            }}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--gold-deep)] underline-offset-2 hover:underline"
          >
            Already confirmed your email? Continue to your account
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </button>
        </div>

        <p className="mt-6 text-[11px] text-muted-foreground">
          Wrong account?{" "}
          <Link to="/login" className="text-[var(--gold-deep)] underline">
            Sign in with a different address
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
